import { Modal } from '../../m/modal';
import { HeaderNavigation } from './types';
import { MobileNavigation } from './MobileNavigation';
import { DesktopNavigation } from './DesktopNavigation';

export class Header {
  el: HTMLElement;
  nav: HeaderNavigation;
  langSwitchToggle?: HTMLButtonElement;
  langSwitchBodyEl?: HTMLDivElement; // the div which holds the contents of the modal to switch between languages
  searchToggle?: HTMLButtonElement;
  navigationToggle?: HTMLButtonElement;

  // the media query to determine which handler to use
  mqLgDown = window.matchMedia('(max-width: 1199px)');

  /**
   *
   * @param el
   */
  constructor(el: HTMLElement) {
    this.el = el;
    this.nav = this.getNavigation();

    //
    this.langSwitchToggle = this.el.querySelector(
      '#btnToggleLanguageSwitch',
    ) as HTMLButtonElement;
    //
    this.langSwitchBodyEl = this.el.querySelector(
      '#language-switch-contents',
    ) as HTMLDivElement;
    //
    this.searchToggle = this.el.querySelector(
      '#btnToggleSearch',
    ) as HTMLButtonElement;
    //
    this.navigationToggle = this.el.querySelector(
      '#btnToggleNavigation',
    ) as HTMLButtonElement;

    this.addEventListeners();
  }

  /**
   * Get the handler for the navigation based on the current viewport.
   */
  getNavigation(): HeaderNavigation {
    // is-mobile
    if (this.mqLgDown.matches) {
      const mobileNavEl = this.el.querySelector(
        '.header__mobile-navigation nav',
      ) as HTMLElement;
      if (!mobileNavEl) {
        throw new Error('Mobile navigation element not found');
      }

      return new MobileNavigation(this.el, mobileNavEl);
    }

    // is-desktop
    const desktopNavEl = this.el.querySelector(
      '.header__navigation nav',
    ) as HTMLElement;
    if (!desktopNavEl) {
      throw new Error('Desktop navigation element not found');
    }

    return new DesktopNavigation(this.el, desktopNavEl);
  }

  /**
   * Close all open elements or chose which to close by passing false
   * to the respective parameter
   *
   * @param closeLangSwitch
   * @param closeSearch
   * @param closeNavigation
   */
  closeAll(closeLangSwitch = true, closeSearch = true, closeNavigation = true) {
    if (closeLangSwitch) {
      this.el.classList.remove('header--language-switch-open');
    }
    if (closeSearch) {
      this.el.classList.remove('header--search-open');
    }
    if (closeNavigation) {
      this.el.classList.remove('header--mobile-navigation-open');

      // call the navigation callback to do some cleanup
      this.nav.onNavigationClose();
    }
  }

  /**
   * Add all required event listeners
   */
  addEventListeners() {
    window.addEventListener('click', (event) => {
      if (!this.el.contains(event.target as Node)) {
        this.handleOutsideClick();
      }
    });

    this.langSwitchToggle?.addEventListener(
      'click',
      this.handleLangSwitchToggleClick.bind(this),
    );
    this.searchToggle?.addEventListener(
      'click',
      this.handleSearchToggleClick.bind(this),
    );
    this.navigationToggle?.addEventListener(
      'click',
      this.handleNavigationToggleClick.bind(this),
    );

    // Close Flyout on ESCAPE on mobile
    window.addEventListener('keydown', (e) => {
      if (e.key == 'Escape' && this.mqLgDown.matches) {
        this.closeAll();
      }
    });

    // handle the resize event to switch between mobile and desktop navigation
    let currIsMobile = this.mqLgDown.matches;
    window.addEventListener('resize', () => {
      if (this.mqLgDown.matches !== currIsMobile) {
        // close all open elements
        this.closeAll();

        // re-init the current navigation handler
        this.nav.destroy();
        this.nav = this.getNavigation();

        // update the current state
        currIsMobile = this.mqLgDown.matches;
      }
    });

    // Remove desktop flyout on mobile
    this.mqLgDown.addListener((e) => {
      if (e.matches) {
        const headerEl = this.el;
        const flyoutActive = this.el.querySelector('.header__flyout--active');
        const navLinkFocus = this.el.querySelector(
          '.header__navigation-link--focus',
        );
        headerEl?.classList.remove('header--navigation-open');
        flyoutActive?.classList.remove('header__flyout--active');
        navLinkFocus?.classList.remove('header__navigation-link--focus');
      }
    });
  }

  /**
   *
   */
  handleOutsideClick() {
    this.closeAll();
  }

  /**
   *
   */
  handleLangSwitchToggleClick() {
    //
    if (!this.langSwitchBodyEl) return;

    this.closeAll(false);
    this.el.classList.toggle('header--language-switch-open');

    //
    const modalTitle =
      this.langSwitchBodyEl.getAttribute('data-modal-title') || '';
    const modal = Modal.create(undefined, undefined, 'language-switcher');
    modal.setTitle(modalTitle);
    modal.setBody(this.langSwitchBodyEl);
    modal.show();
  }

  /**
   *
   */
  handleSearchToggleClick() {
    //
    this.closeAll(true, false);
    this.el.classList.toggle('header--search-open');

    // focus the search input field
    if (this.el.classList.contains('header--search-open')) {
      const input = this.el.querySelector('.aa-Input') as HTMLInputElement;
      if (input) {
        input.focus();
      }
    }
  }

  /**
   *
   */
  handleNavigationToggleClick() {
    //
    this.closeAll(true, true, false);

    // call the navigation callback to do some cleanup
    if (this.el.classList.contains('header--mobile-navigation-open')) {
      this.nav.onNavigationClose();
    }

    this.el.classList.toggle('header--mobile-navigation-open');
  }
}
